import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Budget, BudgetDraft } from "../types/budget.tsx";

const FALLBACK_USER_ID = "guest";
const DEFAULT_CURRENCY = "AED";

const readUserId = () => {
  if (typeof window === "undefined") return FALLBACK_USER_ID;
  try {
    const raw = window.localStorage.getItem("user");
    if (!raw) return FALLBACK_USER_ID;
    const parsed = JSON.parse(raw);
    return parsed?.id || FALLBACK_USER_ID;
  } catch {
    return FALLBACK_USER_ID;
  }
};

const storageKey = (userId: string) => `budgets:${userId || FALLBACK_USER_ID}`;

const normalizeBudget = (input: any): Budget | null => {
  if (!input) return null;
  const id = typeof input.id === "string" && input.id.trim() !== "" ? input.id : uuidv4();
  const category = typeof input.category === "string" && input.category.trim() !== "" ? input.category : "";
  if (!category) return null;
  const rawTarget = Number(input.target);
  const target = Number.isFinite(rawTarget) ? Number(rawTarget.toFixed(2)) : 0;
  const currency =
    typeof input.currency === "string" && input.currency.trim() !== ""
      ? input.currency.toUpperCase()
      : DEFAULT_CURRENCY;
  const createdAt = typeof input.createdAt === "string" ? input.createdAt : new Date().toISOString();
  const updatedAt = typeof input.updatedAt === "string" ? input.updatedAt : createdAt;
  return {
    id,
    category,
    target,
    currency,
    createdAt,
    updatedAt,
  };
};

const loadBudgets = (userId: string): Budget[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => normalizeBudget(item))
        .filter((item): item is Budget => item != null);
    }
  } catch {
    /* swallow */
  }
  return [];
};

export const useBudgets = () => {
  const [userId, setUserId] = useState<string>(() => readUserId());
  const [budgets, setBudgets] = useState<Budget[]>(() => loadBudgets(readUserId()));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "user") {
        setUserId(readUserId());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setBudgets(loadBudgets(userId));
  }, [userId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(budgets));
    } catch {
      /* swallow */
    }
  }, [budgets, userId]);

  const ensureUserSync = useCallback(() => {
    const current = readUserId();
    if (current !== userId) {
      setUserId(current);
      return current;
    }
    return userId;
  }, [userId]);

  const addBudget = useCallback(
    (draft: BudgetDraft) => {
      ensureUserSync();
      const now = new Date().toISOString();
      const target = Number(draft.target);
      const currency = (draft.currency || DEFAULT_CURRENCY).toUpperCase();
      setBudgets((prev) => [
        {
          id: uuidv4(),
          category: draft.category,
          target: Number.isFinite(target) ? Number(target.toFixed(2)) : 0,
          currency,
          createdAt: now,
          updatedAt: now,
        },
        ...prev.filter((item) => item != null),
      ]);
    },
    [ensureUserSync]
  );

  const updateBudget = useCallback(
    (id: string, patch: Partial<Omit<Budget, "id" | "createdAt">>) => {
      ensureUserSync();
      setBudgets((prev) =>
        prev.map((budget) => {
          if (budget.id !== id) return budget;
          const nextTarget =
            patch.target !== undefined && patch.target !== null
              ? Number(patch.target)
              : budget.target;
          const nextCurrency =
            patch.currency && patch.currency.trim() !== ""
              ? patch.currency.toUpperCase()
              : budget.currency;
          return {
            ...budget,
            ...patch,
            target: Number.isFinite(nextTarget) ? Number(nextTarget.toFixed(2)) : budget.target,
            currency: nextCurrency,
            updatedAt: new Date().toISOString(),
          };
        })
      );
    },
    [ensureUserSync]
  );

  const removeBudget = useCallback(
    (id: string) => {
      ensureUserSync();
      setBudgets((prev) => prev.filter((budget) => budget.id !== id));
    },
    [ensureUserSync]
  );

  const upsertBudget = useCallback(
    (payload: BudgetDraft & { id?: string }) => {
      if (payload.id) {
        updateBudget(payload.id, payload);
        return;
      }
      addBudget(payload);
    },
    [addBudget, updateBudget]
  );

  const byCategory = useMemo(() => {
    return budgets.reduce<Record<string, Budget>>((acc, budget) => {
      acc[`${budget.category}:${budget.currency}`] = budget;
      return acc;
    }, {});
  }, [budgets]);

  return {
    budgets,
    addBudget,
    updateBudget,
    removeBudget,
    upsertBudget,
    byCategory,
  };
};
